import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageLayout from "@/components/feature/PageLayout";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/videoData";
import { videos } from "@/mocks/videoData";
import { categories } from "@/mocks/videoData";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = articles.find((a) => a.id === articleId);
  const video = videos.find((v) => v.id === articleId);

  if (!article && !video) {
    return (
      <PageLayout
        seo={
          <SEOHead
            title="页面未找到"
            description="抱歉，您访问的页面不存在。"
            keywords="苹果视频"
          />
        }
      >
        <Navbar />
        <div className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-600 mb-4">页面未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </PageLayout>
    );
  }

  let title = "";
  let description = "";
  let categorySlug = "";
  let categoryName = "";
  let thumbnail = "";
  let content = "";
  let date = "";
  let author = "";
  let rating = 0;
  let year = 0;
  let tags: string[] = [];
  let director = "";
  let cast: string[] = [];
  let duration = "";

  if (article) {
    title = article.title;
    description = article.summary;
    categorySlug = article.category;
    const cat = categories.find((c) => c.slug === categorySlug);
    categoryName = cat ? cat.name : "资讯";
    thumbnail = article.thumbnail;
    content = article.content;
    date = article.date;
    author = article.author;
  } else if (video) {
    title = video.title;
    description = video.description;
    categorySlug = video.category;
    const cat = categories.find((c) => c.slug === categorySlug);
    categoryName = cat ? cat.name : video.category;
    thumbnail = video.thumbnail;
    content = `<p>${video.description}</p>`;
    rating = video.rating;
    year = video.year;
    tags = video.tags;
    director = video.director;
    cast = video.cast;
    duration = video.duration;
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: description,
    image: thumbnail,
    datePublished: date || `${year}-01-01`,
    author: {
      "@type": "Organization",
      name: author || "苹果视频编辑部",
    },
    publisher: {
      "@type": "Organization",
      name: "苹果视频",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.apple-video.com/article/${id}`,
    },
  };

  return (
    <PageLayout
      seo={
        <SEOHead
          title={`${title} - 苹果视频`}
          description={description.length > 150 ? description.substring(0, 150) + "..." : description}
          keywords={`${title},苹果视频,${categoryName},免费在线观看,高清,流畅`}
          canonicalUrl={`/article/${id}`}
          ogType="article"
          ogImage={thumbnail}
        />
      }
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <div className="pt-20">
        <article className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: categoryName, href: `/category/${categorySlug}` },
                { label: title },
              ]}
            />

            <header className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading leading-tight">
                {title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                {date && <span><i className="ri-calendar-line mr-1"></i>{date}</span>}
                {author && <span><i className="ri-user-line mr-1"></i>{author}</span>}
                {categoryName && (
                  <Link to={`/category/${categorySlug}`} className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                    <i className="ri-price-tag-3-line mr-1"></i>{categoryName}
                  </Link>
                )}
              </div>

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-900 px-2.5 py-1 rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {thumbnail && (
              <div className="relative overflow-hidden rounded-xl mb-8 aspect-video">
                <img
                  src={thumbnail}
                  alt={title}
                  className="w-full h-full object-cover"
                  title={`${title} - 苹果视频`}
                />
                {rating > 0 && (
                  <div className="absolute bottom-4 left-4 bg-foreground-950/80 text-background-50 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    {rating} 分
                  </div>
                )}
              </div>
            )}

            {video && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 p-4 bg-background-100 rounded-xl">
                {director && (
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">{director}</p>
                  </div>
                )}
                {cast.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">{cast.join(" / ")}</p>
                  </div>
                )}
                {duration && (
                  <div>
                    <span className="text-xs text-foreground-500">时长</span>
                    <p className="text-sm font-medium text-foreground-950 mt-0.5">{duration}</p>
                  </div>
                )}
              </div>
            )}

            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <h3 className="text-lg font-semibold text-foreground-950 font-heading mb-4">相关推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {videos
                  .filter((v) => v.category === categorySlug && v.id !== articleId)
                  .slice(0, 4)
                  .map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-video mb-2">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          title={v.title}
                          loading="lazy"
                        />
                      </div>
                      <h4 className="text-xs font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {v.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-0.5">{v.year} · {v.rating}分</p>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </div>
      <Footer />
    </PageLayout>
  );
}